import {
    DataGrid,
    DataGridProps,
    GridColumnVisibilityModel,
    GridDensity,
    GridSelectionModel,
    GridSortItem,
    GridSortModel,
    GridValueGetterParams,
} from '@mui/x-data-grid';
import { getObjectValueByPath, migrateSessionData } from 'helpers/general';
import React, { useEffect, useMemo, useState } from 'react';
import { NapiResultType } from 'types';
import { DataTableReportPreset } from './components/GridToolbarPresetSelector/GridToolbarPresetSelector';
import { DataTableToolBar, DataTableToolBarProps } from './DataTableToolBar';
import styles from './DataTable.module.scss';
import { useUserSession } from 'hooks';
import { mapUserSessionToMuiDataGrid } from './opsMapping';
import { useUi } from 'hooks/useUi';
import { DataGridPlaceholder } from 'components/DataGridPlaceholder/DataGridPlaceholder';
import { UserSessionKeys } from 'types/User/firebaseTypes';
import { GridToolbarExportButtonProps } from './components/GridToolbarExportButton/GridToolbarExportButton';

export type DataTableColumn = Omit<
    DataGridProps['columns'][number],
    'headerName'
> & {
    headerName: string;
    /** used for mapping ops portal session. Must be a unique name among other columns in the table. Cannot contain periods or spaces and some other special characters. */
    fbKey?: string;
    /** Used to indicate whether or not a column is visible by default. If a user's session indicates that the column should/shouldn't be visible, that value takes priority. This value will inform the "Reset" button on the column options regardless of session settings. */
    hideByDefault?: boolean;
};
export interface DataTableProps<TData = Record<string, any>>
    extends Omit<
        DataGridProps,
        | 'rows'
        | 'columns'
        | 'csvOptions'
        | 'onSelectionModelChange'
        | 'selectionModel'
    > {
    /** column id for our internal sorting and visibility models. Must be provided for them to work properly with local storage and user session data. */
    id: UserSessionKeys;
    columns: DataTableColumn[];
    data?: TData | null;
    selectableListMode?: boolean;
    /** format incoming table data to any shape for rows*/
    dataFormatter?: (data: any) => Record<string, any>[];
    getRowCount?: (data: any) => number;
    toolbarComponents?: DataTableToolBarProps['toolbarComponents'];
    bulkEditModal?: DataTableToolBarProps['bulkEditModal'];
    defaultColumnSortingOrder?: DataTableToolBarProps['defaultColumnSortingOrder'];
    presets?: DataTableToolBarProps['presets'];
    hideToolbar?: DataTableToolBarProps['hideToolbar'];
    hideSearch?: DataTableToolBarProps['hideSearch'];
    onPageChange?: React.Dispatch<React.SetStateAction<number>>;
    onPageSizeChange?: React.Dispatch<React.SetStateAction<number>>;
    onSortModelChange?: React.Dispatch<React.SetStateAction<GridSortItem[]>>;
    csvOptions?: GridToolbarExportButtonProps['csvOptions'];
    onCurrentPresetChange?: React.Dispatch<
        React.SetStateAction<DataTableReportPreset | null>
    >;
    /** Optional parameter to autofocus the search bar on the MUI Data Grid toolbar. This can be useful when server mode is enabled for search/sort/filter which causes the grid to rerender and the search bar to lose focus while user is still typing. */
    autoFocusSearch?: boolean;
    sortModel?: GridSortModel;
    // if we need to interact with the selection model outside of the DataTable we will use this function
    setCustomSelectionModel?: React.Dispatch<
        React.SetStateAction<GridSelectionModel>
    >;
    customSelectionModel?: GridSelectionModel;
}

/**  Abstract table wrapper for @mui/x-data-grid component
 *  @note Please see {@see data-structures/sharedColumns} for shared columns before making custom columns.
 *  @param {string} id - column id for our internal sorting and visibility models. Must be provided for them to work properly with local storage and user session data.
 *  @param {DataGridProps} ...DataGridProps Extension of DataGridProps
 *  @param {TData} data data in the shape of a napi api result
 *  @param {boolean} loading loading boolean for rendering purposes
 *  @param {Function} dataFormatter optional data formatter to transform any given data to a specific format (DEFAULT NAPI)
 *  @param {React.ReactNode} toolbarComponents optional components to render in the toolbar
 *  @param {React.ReactNode} bulkEditModal optional modal for bulk editing given rows.
 *  @param {DataTableReportPreset[]} presets list of presets for a chooser to choose from that will determinate what rows render
 *  @param {string[]} defaultColumnSortingOrder list of fields to revert to when the sorting order is reset in the {@see GridToolbarColumnsButton} component
 *  @param {boolean} autoFocusSearch Optional parameter to autofocus the search bar on the MUI Data Grid toolbar. This can be useful when server mode is enabled for search/sort/filter which causes the grid to rerender and the search bar to lose focus while user is still typing.
 */
export function DataTable<TData extends Record<string, any>>({
    id,
    data,
    selectableListMode,
    columns,
    loading,
    pageSize = 50,
    onPageChange,
    onPageSizeChange,
    onSortModelChange,
    onCurrentPresetChange,
    rowsPerPageOptions = [10, 25, 50, 100],
    dataFormatter = (data: NapiResultType<TData[]>) => data.results ?? [],
    getRowCount = (data: NapiResultType<TData[]>) =>
        data.total ?? data.results?.length ?? 0,
    // for testing purposes, pass in custom getRowId, otherwise leave this implementation for unique keys
    getRowId = (row) => JSON.stringify(row),
    toolbarComponents,
    bulkEditModal,
    presets,
    hideToolbar,
    hideSearch,
    autoFocusSearch = false,
    defaultColumnSortingOrder = columns.map((c) => c.field),
    csvOptions,
    sortModel,
    setCustomSelectionModel,
    customSelectionModel,
    ...props
}: DataTableProps<TData>) {
    const { ui } = useUi();
    const defaultVisibilityModel = columns.reduce(
        (prev, curr) => ({
            ...prev,
            [curr.field]: curr.hideByDefault ? false : true,
        }),
        {}
    );
    const session = useUserSession();

    const [pageSizeCount, setPageSizeCount] = useState(pageSize);
    const [page, setPage] = useState(0);

    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<GridColumnVisibilityModel>(defaultVisibilityModel);
    const [columnSortingOrder, setColumnSortingOrder] = useState<string[]>(
        defaultColumnSortingOrder ? [...defaultColumnSortingOrder] : []
    );
    const [checkboxSelectionEnabled, setCheckboxSelectionEnabled] =
        useState(false);
    const [currentPreset, setCurrentPreset] =
        useState<DataTableReportPreset | null>(null);
    const [density] = useState<GridDensity>('compact');
    const rows = useMemo(
        () => (data ? dataFormatter(data) : []),
        [data, dataFormatter]
    );
    const rowCount = useMemo(
        () => (data ? getRowCount(data) : 0),
        [data, getRowCount]
    );

    const mappedColumns = useMemo(() => {
        const filterColumns = (col: DataTableColumn) => {
            if (currentPreset) {
                return currentPreset.cols.includes(col.field);
            } else {
                return columnVisibilityModel[col.field] === true;
            }
        };

        const filteredColumns = columns.filter(filterColumns).map((col) => {
            const flex = col.flex ?? 2;
            return {
                ...col,
                flex,
                minWidth: col.minWidth ?? 150,
                sortingOrder: col.sortingOrder ?? ['desc', 'asc'],
                valueGetter:
                    col.valueGetter ??
                    ((params: GridValueGetterParams) =>
                        getObjectValueByPath(params.row, col.field)),
            };
        });

        if (columnSortingOrder.length) {
            filteredColumns.sort((a, b) => {
                const columnA = columnSortingOrder?.findIndex(
                    (value) => value === a.field
                );
                const columnB = columnSortingOrder?.findIndex(
                    (value) => value === b.field
                );

                return columnA > columnB ? 1 : -1;
            });
        }

        return filteredColumns;
    }, [columns, columnSortingOrder, currentPreset, columnVisibilityModel]);

    // on page load we receive the visibility model previously set by the user
    useEffect(() => {
        if (session) {
            // temporary mapping for compatibility with ops portal
            const { visibilityModel, sortingOrder } =
                mapUserSessionToMuiDataGrid(session, id, columns);

            if (visibilityModel) {
                setColumnVisibilityModel(visibilityModel);
            }

            if (sortingOrder) {
                setColumnSortingOrder(
                    migrateSessionData(
                        sortingOrder,
                        defaultColumnSortingOrder
                    ) as string[]
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, id, session]); // have to ignore defaultColumnSortingOrder

    useEffect(() => {
        if (onCurrentPresetChange) onCurrentPresetChange(currentPreset ?? null);
        if (currentPreset) {
            const newColumnVisibilityModel = currentPreset.cols.reduce(
                (prev, curr) => ({ ...prev, [curr]: true }),
                {}
            );
            setColumnVisibilityModel(newColumnVisibilityModel);
        }
    }, [currentPreset, onCurrentPresetChange]);

    const onPageSizeChangeHandler = (pageSize: number) => {
        setPageSizeCount(pageSize > 100 ? 100 : pageSize);
        if (onPageSizeChange) onPageSizeChange(pageSize);
    };

    const [previousSelectionModel, setPreviousSelectionModel] =
        useState<GridSelectionModel>([]);

    const onSelectionModelChangeHandler = async (
        newSelectionModel: GridSelectionModel
    ) => {
        if (checkboxSelectionEnabled) {
            const newSelectedRows = newSelectionModel.map((model) => {
                if (typeof model === 'string') {
                    try {
                        return JSON.parse(model);
                    } catch (e) {
                        return model;
                    }
                }
                return null;
            });

            // Determine what rows were added
            const addedRows = newSelectedRows.filter(
                (id) => !previousSelectionModel.includes(id)
            );

            // Determine what rows were removed and are currently visible
            const removedRows = previousSelectionModel.filter(
                (id) =>
                    !newSelectedRows.includes(id) &&
                    rows.some((row) => getRowId(row) === id)
            );

            // Update the customSelectionModel
            if (setCustomSelectionModel) {
                setCustomSelectionModel((prevModel) => {
                    // Start with rows that were not removed
                    const updatedModel = prevModel.filter(
                        (id) => !removedRows.includes(id)
                    );
                    // Add newly added rows
                    return [...new Set([...updatedModel, ...addedRows])];
                });
            }

            // Update the previous selection model for the next change comparison
            setPreviousSelectionModel(newSelectedRows);
        }
    };

    useEffect(() => {
        if (customSelectionModel?.length === 0) {
            setPreviousSelectionModel([]);
        }
    }, [customSelectionModel]);

    return ui.animatingViewTransition ? (
        <DataGridPlaceholder />
    ) : (
        <DataGrid
            className={styles.dataTable}
            rows={rows}
            loading={loading}
            columns={mappedColumns}
            pageSize={pageSizeCount}
            page={page}
            sortModel={sortModel}
            onPageChange={(newPage) => {
                setPage(newPage);
                if (onPageChange) onPageChange(newPage);
            }}
            sx={{
                '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                    py: '.25rem',
                },
                '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                    py: '.5rem',
                },
                '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
                    py: '.75rem',
                },
            }}
            getRowHeight={() => 'auto'}
            rowsPerPageOptions={rowsPerPageOptions}
            onPageSizeChange={onPageSizeChangeHandler}
            onSortModelChange={(changes) => {
                if (onSortModelChange) onSortModelChange(changes);
            }}
            checkboxSelection={checkboxSelectionEnabled}
            onSelectionModelChange={onSelectionModelChangeHandler}
            initialState={
                props.initialState ?? {
                    sorting: {
                        sortModel: [{ field: 'name', sort: 'asc' }],
                    },
                }
            }
            components={{
                Toolbar: DataTableToolBar,
            }}
            componentsProps={{
                toolbar: {
                    gridId: id,
                    checkboxSelectionEnabled,
                    setCheckboxSelectionEnabled,
                    setCurrentPreset,
                    currentPreset,
                    presets,
                    toolbarComponents,
                    bulkEditModal,
                    columnVisibilityModel,
                    setColumnVisibilityModel,
                    columnSortingOrder,
                    setColumnSortingOrder,
                    defaultColumnSortingOrder,
                    columns,
                    csvOptions: {
                        ...csvOptions,
                        fileName: csvOptions?.fileName,
                    },
                    hideToolbar,
                    hideSearch,
                    autoFocusSearch,
                    selectionModel: customSelectionModel,
                    setSelectionModel: setCustomSelectionModel,
                    printOptions: { disableToolbarButton: true },
                },
            }}
            density={density}
            columnVisibilityModel={!currentPreset ? columnVisibilityModel : {}}
            // a mui race condition occurs between our bulk edit implementation and the mui DataGrid api. This circumvents that
            isRowSelectable={(params) =>
                (selectableListMode || checkboxSelectionEnabled) && !!params.id
            }
            getRowId={getRowId}
            rowCount={rowCount}
            disableColumnMenu
            selectionModel={customSelectionModel} // Ensure this is directly applied
            {...props}
        />
    );
}
